import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Container, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { IBatch, IBatchPayeeOption, IBatchSearchRequest } from "../../../types/shipment";
import { ISelectOption, ISelectOptionWithEntity } from "../../../types/list";

import { BatchService } from "../../../services/batchService";

import { AppConfig } from "../../../config/appConfig";
import { useUserContext } from "../../../contexts/userContext";

import BatchTable from "../../tables/batchTable";
import Navigation from "../../header";
import PermissionGate from "../../permissionGate";
import Spinner from "../../spinner";

import "../../../styles/entity/batchList.scss";
import { ICustomerLight } from "../../../types/customer";
import { UtilityService } from "../../../services/utilityService";
import { CustomerService } from "../../../services/customerService";

const BatchList = () => {
	const navigate = useNavigate();
	const userContext = useUserContext();
	const [searchResults, setSearchResults] = useState<IBatch[]>();
	const [filteredSearchResults, setFilteredSearchResults] = useState<IBatch[]>();

	//Filtering
	const [statusOptions, setStatusOptions] = useState(["Reconciling", "Escalated", "Reviewing", "Auditing", "QB Entry", "QB Audit", "Complete"]);
	const [availableCustomers, setAvailableCustomers] = useState<ICustomerLight[]>([]);
	const [customerOptions, setCustomerOptions] = useState<ISelectOption[]>([]);
	const [availablePayees, setAvailablePayees] = useState<IBatchPayeeOption[]>([]);
	const [payeeOptions, setPayeeOptions] = useState<ISelectOptionWithEntity[]>([]);
	const [monthOptions, setMonthOptions] = useState<string[]>([]);
	const [monthFilter, setMonthFilter] = useState('');
	const [batchFilter, setBatchFilter] = useState('');
	const [bolFilter, setBolFilter] = useState('');
	const [invoiceFilter, setInvoiceFilter] = useState('');
	const [payeeFilter, setPayeeFilter] = useState('');
	const [customerFilter, setCustomerFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');

	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [isLoadingInitialCustomers, setIsLoadingInitialCustomers] = useState<boolean>(true);
	const [isLoadingInitialPayees, setIsLoadingInitialPayees] = useState<boolean>(true);
	const [isLoadingInitialMonths, setIsLoadingInitialMonths] = useState<boolean>(true);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const [userHasSearched, setUserHasSearched] = useState(false);
	const forceSearchKeys = ['Enter', 'NumpadEnter'];

	useEffect(() => {
		loadPayees();
		loadCustomers();
		loadMonths();
	}, [])

	useEffect(() => {
		if (!isLoadingInitialMonths) {
			loadBatchData();
		}
	}, [isLoadingInitialMonths, monthFilter])

	useEffect(() => {
		if (!searchResults) return;
		let newData = searchResults.filter((item) => {
			if (payeeFilter) {
				const parts = payeeFilter.split(':');
				if (!(item.payeeId == parts[0] && item.payeeType == parts[1])) return false;
			}
			if (customerFilter) {
				if (
					!item.customers?.some(c => c.id == customerFilter) && 
					!(customerFilter == "empty" && item.customers?.length == 0) &&
					!(customerFilter == "multiple" && item.customers?.length > 1)
				) return false;
			}
			if (statusFilter && statusFilter != "All" && item.status != statusFilter) {
				return false;
			}
			return true;
		}).slice();

		const customers: ICustomerLight[] = [];
		const payees: IBatchPayeeOption[] = [];
		for (const batch of newData) {
			if (!payees.find(p => p.entityId === +batch.payeeId && p.entityType === batch.payeeType)) {
				payees.push({
					entityId: +batch.payeeId,
					entityType: batch.payeeType,
					entityName: batch.payeeName,
					localId: 0
				});
			}

			for (const customer of batch.customers) {
				if (!customers.find(c => c.id == customer.id)) {
					customers.push(customer);
				}
			}
		}
		customers.sort((a, b) => UtilityService.compareStrings(a.name, b.name));

		setAvailablePayees(payees);
		setAvailableCustomers(customers);
		setFilteredSearchResults(newData);
	}, [searchResults, payeeFilter, customerFilter, statusFilter])

	const loadBatchData = async () => {
		setErrorMessage(undefined);

		try {
			const initialSearchRequest: IBatchSearchRequest = {
				month: monthFilter,
				batch: batchFilter,
				bol: bolFilter,
				invoice: invoiceFilter
			}
			await searchBatches(initialSearchRequest);
		}
		catch (error) {
			console.log('BatchService.searchBatches: ERROR', error);
			if (error?.code == "ERR_NETWORK") {
				setErrorMessage(AppConfig.apiConnectionErrorMessage)
			}
		}
	}

	const handleMonthChange = (value: string) => {
		if (value) {
			setMonthFilter(value);
		}
	}

	const handleTextboxKeydown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		// Force a search when certian keys are pressed, like Enter
		if (forceSearchKeys.includes(event.code)) {
			setUserHasSearched(true);
			setPayeeFilter('');
			setCustomerFilter('');
			setStatusFilter('');
			loadBatchData();
		}
	};

	const loadPayees = async () => {
		BatchService.getPayees()
			.then((response) => {
				console.log('BatchService.getPayees: SUCCESS', response);
				const payees: ISelectOptionWithEntity[] = response.data.map((item: any) => {
					return {
						name: item.name,
						value: item.id,
						type: item.type
					};
				});

				setPayeeOptions(payees);
			})
			.catch((error) => {
				console.log('ShipmentService.getSearchValueSets: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setIsLoadingInitialPayees(false);
			});
	};

	const loadCustomers = async () => {
		CustomerService.getActiveCustomerOptions()
			.then((response) => {
				console.log('CustomerService.getActiveCustomerOptions: SUCCESS', response);
				const customers: ISelectOption[] = response.data.map((item: ISelectOption) => {
					return {
						name: item.name,
						value: item.value
					};
				});

				setCustomerOptions(customers);
			})
			.catch((error) => {
				console.log('CustomerService.getActiveCustomerOptions: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setIsLoadingInitialCustomers(false);
			});
	};

	const loadMonths = async () => {
		BatchService.getExistingMonths()
			.then((response) => {
				console.log('BatchService.getExistingMonths: SUCCESS', response);
				setMonthOptions(response.data);

				if (isLoadingInitialMonths) {
					setMonthFilter(response.data.length > 0 ? response.data[0] : '');
				}
			})
			.catch((error) => {
				console.log('BatchService.getExistingMonths: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setIsLoadingInitialMonths(false);
			});
	}

	const searchBatches = async (request: IBatchSearchRequest) => {
		setErrorMessage(undefined);
		setIsSearching(true);
		setPayeeFilter('');
		setCustomerFilter('');
		setAvailableCustomers([]);
		setAvailablePayees([]);

		request.month = monthFilter;
		request.batch = batchFilter;
		request.bol = bolFilter;
		request.invoice = invoiceFilter;
		BatchService.searchBatches(request)
			.then((response) => {
				console.log('BatchService.searchBatches: SUCCESS', response);
				const batches = BatchService.mapBatches(response.data);
				setSearchResults(batches);
			})
			.catch((error) => {
				console.log('BatchService.searchBatches: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setIsSearching(false);
			});
	};

	const formatBatchMonth = (month: string) => {
		if (!month || month.length != 6) return "N/A";
		const tempDate = month.substring(0, 4) + ' ' + month.substring(4);
		const date = new Date(tempDate);
		return date.toLocaleString('default', { month: 'long', year: 'numeric' });
	}

	return (
		<div className="hwi-batch-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageShipments}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Spinner dependency={isLoadingInitialMonths} text="Loading...">
							<section className="hwi-entity-search-bar">
								<div className="search-header">
									<div className="title">Search Batched Shipments</div>
									<div className="controls">
										<FormControl size="small" className="month-field">
											<InputLabel>Month</InputLabel>
											<Select
												value={monthFilter}
												label="Month"
												onChange={(event: SelectChangeEvent) => { setMonthFilter(event.target.value) }}
											>
												{monthOptions.map((item, index) => (
													<MenuItem value={item} key={index}>{formatBatchMonth(item)}</MenuItem>
												))}
											</Select>
										</FormControl>
										<div className="button">
											<Button variant="outlined" fullWidth={true} onClick={() => { setUserHasSearched(true); loadBatchData(); }}>Search</Button>
										</div>
										<div className="button">
											<Button variant="outlined" fullWidth={true} disabled={!userContext.permissions?.canManageBatches} onClick={() => navigate("/batch/add")}>New Batch</Button>
										</div>
									</div>
								</div>
								<div className="search-options">
									<FormControl size="small" className="batch-field">
										<TextField
											label="Batch #"
											name="batch"
											value={batchFilter}
											size="small"
											onKeyDown={(event) => handleTextboxKeydown(event)}
											onChange={(event) => { setBatchFilter(event.target.value); }}>
										</TextField>
									</FormControl>
									<FormControl size="small" className="invoice-field">
										<TextField
											label="Invoice #"
											name="invoice"
											value={invoiceFilter}
											size="small"
											onKeyDown={(event) => handleTextboxKeydown(event)}
											onChange={(event) => { setInvoiceFilter(event.target.value); }}>
										</TextField>
									</FormControl>
									<FormControl size="small" className="payee-field">
										<InputLabel>Payee{isLoadingInitialPayees ? " (Loading...)" : ""}</InputLabel>
										<Select
											value={payeeFilter}
											label="Payee"
											onChange={(event: SelectChangeEvent) => { setPayeeFilter(event.target.value) }}
											disabled={isLoadingInitialPayees}
										>
											<MenuItem value="" key="-1">All</MenuItem>
											{payeeOptions.filter(p => availablePayees.find(ap => ap.entityId === +p.value && ap.entityType === p.type)).map((item, index) => (
												<MenuItem value={`${item.value}:${item.type}`} key={index}>{item.name} ({item.type})</MenuItem>
											))}
										</Select>
									</FormControl>
									<FormControl size="small" className="bol-field">
										<TextField
											label="BOL #"
											name="bol"
											value={bolFilter}
											size="small"
											onKeyDown={(event) => handleTextboxKeydown(event)}
											onChange={(event) => { setBolFilter(event.target.value); }}>
										</TextField>
									</FormControl>
									<FormControl size="small" className="customer-field">
										<InputLabel>Customer{isLoadingInitialCustomers ? " (Loading...)" : ""}</InputLabel>
										<Select
											value={customerFilter}
											label="Customer"
											onChange={(event: SelectChangeEvent) => { setCustomerFilter(event.target.value) }}
											disabled={isLoadingInitialCustomers}
										>
											<MenuItem value="" key="-1">All</MenuItem>
											{filteredSearchResults?.some(r => r.customers?.length == 0) && <MenuItem value="empty" key="-2">* None</MenuItem>}
											{filteredSearchResults?.some(r => r.customers?.length > 1) && <MenuItem value="multiple" key="-3">* Multiple</MenuItem>}
											{customerOptions.filter(c => availableCustomers.find(ac => ac.id == c.value)).map((item, index) => (
												<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
											))}
										</Select>
									</FormControl>
									<FormControl size="small" className="status-field">
										<InputLabel>Status</InputLabel>
										<Select
											value={statusFilter}
											label="Status"
											onChange={(event: SelectChangeEvent) => { setStatusFilter(event.target.value) }}
										>
											<MenuItem value="" key="-1">All</MenuItem>
											{statusOptions.map((item, index) => (
												<MenuItem value={item} key={index}>{item}</MenuItem>
											))}
										</Select>
									</FormControl>
								</div>
							</section>
							<section className="results">
								{errorMessage &&
									<Alert className="alert-error"
										severity="error">
										{errorMessage}
									</Alert>
								}
								<Spinner dependency={isSearching} text="Searching...">
									{!errorMessage &&
										<BatchTable data={filteredSearchResults} payee={payeeFilter} customer={customerFilter} status={statusFilter}></BatchTable>
									}
								</Spinner>
							</section>
							{/* <Json data={gridView}></Json> */}
						</Spinner>
					</LocalizationProvider>
				</PermissionGate>
			</Container>
		</div>
	);
};

export default BatchList;